import type { ArtefactType } from './artefact-type'

/**
 * The artefact-template catalogue as the "New artefact" dialog shows it (AB#270) — the browser half
 * of `GET /v1/artefact-templates` (backend/domain/template-catalogue.ts). Categories are the hidden
 * template library's folders (at most 2 levels); templates are its artefacts. Both arrive in the
 * library explorer's order, which every function here keeps.
 */

export interface TemplateCategory {
  id: string
  title: string
  /** `null` = a top-level category. */
  parentId: string | null
}

export interface TemplateEntry {
  id: string
  name: string
  type: ArtefactType
  description: string
  version: number
  /** `null` = the library root. */
  categoryId: string | null
}

export interface TemplateCatalogue {
  categories: TemplateCategory[]
  templates: TemplateEntry[]
}

/** A category of the picker's tree: its sub-categories, then its own templates (explorer order). */
export interface TemplateNode {
  category: TemplateCategory
  /** 1 for a top-level category. */
  depth: number
  children: TemplateNode[]
  templates: TemplateEntry[]
}

export interface TemplateTree {
  roots: TemplateNode[]
  /** Templates at the library root, listed after the categories — as the explorer does. */
  rootTemplates: TemplateEntry[]
}

/** Case- and accent-insensitive form for matching ("Évaluation" ≈ "evaluation"). */
export function foldText(s: string): string {
  return s.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase()
}

/** The category filter's options: every category with its depth, parents before children. */
export function categoryOptions(categories: readonly TemplateCategory[]): Array<{ category: TemplateCategory; depth: number }> {
  const known = new Set(categories.map((c) => c.id))
  const out: Array<{ category: TemplateCategory; depth: number }> = []
  const visit = (parentId: string | null, depth: number): void => {
    for (const c of categories) {
      const parent = c.parentId !== null && known.has(c.parentId) ? c.parentId : null
      if (parent !== parentId) continue
      out.push({ category: c, depth })
      // The library holds 2 levels; the cap only guards against a malformed (cyclic) catalogue.
      if (depth < 5) visit(c.id, depth + 1)
    }
  }
  visit(null, 1)
  return out
}

/** `categoryId` and every category below it. */
function subtreeOf(categories: readonly TemplateCategory[], categoryId: string): Set<string> {
  const ids = new Set([categoryId])
  for (let grew = true; grew; ) {
    grew = false
    for (const c of categories) {
      if (c.parentId !== null && ids.has(c.parentId) && !ids.has(c.id)) {
        ids.add(c.id)
        grew = true
      }
    }
  }
  return ids
}

/**
 * The tree the picker renders, narrowed by a search `query` (name or description, case- and
 * accent-insensitive) and a `categoryId` (that category and its sub-categories; null = all).
 * Unfiltered, every category shows — an empty one included, like an empty explorer folder. Filtered,
 * only the categories leading to a match remain. A category whose parent is unknown sits at the top
 * level and a template whose category is unknown at the root: nothing becomes invisible.
 */
export function buildTemplateTree(
  catalogue: TemplateCatalogue,
  filter: { query?: string; categoryId?: string | null } = {},
): TemplateTree {
  const { categories } = catalogue
  const known = new Set(categories.map((c) => c.id))
  const q = foldText(filter.query?.trim() ?? '')
  const scope = filter.categoryId && known.has(filter.categoryId) ? subtreeOf(categories, filter.categoryId) : null
  const filtering = q !== '' || scope !== null

  const matches = (tpl: TemplateEntry): boolean => {
    if (q && !foldText(tpl.name).includes(q) && !foldText(tpl.description).includes(q)) return false
    if (scope) return tpl.categoryId !== null && scope.has(tpl.categoryId)
    return true
  }
  const kept = catalogue.templates.filter(matches)

  const build = (category: TemplateCategory, depth: number): TemplateNode | null => {
    // The library holds 2 levels; the cap only guards against a malformed (cyclic) catalogue.
    const children =
      depth < 5
        ? categories
            .filter((c) => c.parentId === category.id)
            .map((c) => build(c, depth + 1))
            .filter((n): n is TemplateNode => n !== null)
        : []
    const templates = kept.filter((tpl) => tpl.categoryId === category.id)
    if (filtering && children.length === 0 && templates.length === 0) return null
    return { category, depth, children, templates }
  }
  const roots = categories
    .filter((c) => c.parentId === null || !known.has(c.parentId))
    .map((c) => build(c, 1))
    .filter((n): n is TemplateNode => n !== null)
  const rootTemplates = kept.filter((tpl) => tpl.categoryId === null || !known.has(tpl.categoryId))
  return { roots, rootTemplates }
}

/** Is there anything at all to show in a tree? */
export function isTreeEmpty(tree: TemplateTree): boolean {
  return tree.roots.length === 0 && tree.rootTemplates.length === 0
}
